import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useField, useFormikContext } from 'formik'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { DATE_AND_TIME_FIELD_MODES } from '../formDefinitions'
import { fetchDateAndTimeFieldSetup } from '../smartFormHelpers'
import { makeStyles, createTheme, ThemeProvider } from '@material-ui/core/styles'
import errorIcon from 'images/overdue.svg'
import { ReactComponent as DatePickerIcon } from 'images/datepicker-icon.svg'

const useStyles = (vstTheme) =>
  makeStyles(() => ({
    root: {},
    labelRoot: {
      position: 'relative',
      transform: 'none !important',
      color: '#1C1C1C',
      fontFamily: 'Roboto',
      fontSize: '16px',
      lineHeight: '20px',
      fontWeight: 500,
      margin: '0 0 7px',
    },
    focusedLabel: {
      color: '#1C1C1C !important',
    },
    labelError: {
      color: '#1C1C1C !important',
    },
    notchedOutline: {
      border: 'none',
    },
    inputRoot: {
      border: '1px solid rgba(0, 0, 0, 0.43)',
      '& > div': {
        color: '#1C1C1C',
        fontFamily: 'Roboto',
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 400,
      },
    },
    editHighlight: {
      boxShadow: 'inset black 0px 0px 0px 2px',
    },
    inputError: {
      borderColor: `${vstTheme?.vstui?.color?.error} !important`,
      '& svg': {
        fill: `${vstTheme?.vstui?.color?.error} !important`,
      },
    },
    inputDisabled: {
      '& svg': {
        fill: 'rgba(0, 0, 0, 0.26) !important',
      },
    },
    helperTextRoot: {
      position: 'relative',
      fontFamily: 'Roboto',
      fontSize: '16px',
      lineHeight: '24px',
      marginLeft: '24px',
    },
    helperTextError: {
      color: '#D21C35 !important',
      '&::before': {
        content: "''",
        backgroundImage: `url(${errorIcon})`,
        transform: 'translate(-20px, 3px)',
        height: '16px',
        width: '16px',
        position: 'absolute',
        top: '0',
      },
    },
    adornmentPosition: {
      '& > button': {
        transform: 'translateX(15px) !important',
      },
    },
    pickerIcon: {
      height: '17px',
      width: '17px',
      fill: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
    },
  }))()

const usePickerTheme = (vstTheme) =>
  createTheme({
    overrides: {
      MuiPickersToolbar: {
        toolbar: {
          backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
      },
      MuiPickersCalendarHeader: {
        switchHeader: {
          backgroundColor: 'white',
        },
        dayLabel: {
          color: `${vstTheme?.vstui?.color?.primary?.getShade(8)}`,
        },
      },
      MuiPickersDay: {
        daySelected: {
          backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
          '&:hover': {
            backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
          },
        },
      },
      MuiButton: {
        textPrimary: {
          color: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
      },
      MuiDialogActions: {
        root: {
          '& button': {
            '&:hover': {
              backgroundColor: '#EEEEEE',
            },
          },
        },
      },
    },
  })

const DatePicker = ({ theme, required, disabled, mode, showEmptyLabel, editedField, ...props }) => {
  const { t } = useTranslation()
  const { values } = useFormikContext()
  const [field, meta, helpers] = useField(props)
  const classes = useStyles(theme)
  const pickerTheme = usePickerTheme(theme)

  const onChange = useCallback(
    (date) => {
      helpers.setValue(date)
      if (meta.error) {
        helpers.setError(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [helpers],
  )

  const fetchFieldLabel = useCallback(() => {
    const { formFields, multipleValuesOption } = fetchDateAndTimeFieldSetup(mode)
    if (mode && values[formFields.select.name] === multipleValuesOption.value) {
      return showEmptyLabel ? '' : t(multipleValuesOption.i18n)
    }
    return t('settingsForm.datePlaceholder')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, mode, values])

  const conditionalAttributes = {}
  if (props.min.length) conditionalAttributes.min = props.min

  return (
    <div className="date-picker">
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <ThemeProvider theme={pickerTheme}>
          <KeyboardDatePicker
            id={props.name}
            defaultValue={props.defaultValue}
            label={props.label}
            value={field.value}
            disabled={disabled}
            onBlur={field.onBlur}
            emptyLabel={fetchFieldLabel()}
            onChange={onChange}
            error={meta.error ? true : false}
            helperText={meta.error ? meta.error : null}
            inputProps={{ name: props.name }}
            classes={{ root: classes.root }}
            required={required}
            keyboardIcon={<DatePickerIcon className={classes.pickerIcon} />}
            autoComplete="off"
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
            inputVariant="outlined"
            format={t('settingsForm.dateFormat') || 'MM-DD-yyyy'}
            InputProps={{
              classes: {
                notchedOutline: classes.notchedOutline,
                root: classNames(classes.inputRoot, { [classes.editHighlight]: editedField }),
                error: classes.inputError,
                disabled: classes.inputDisabled,
              },
            }}
            InputLabelProps={{
              classes: {
                root: classes.labelRoot,
                focused: classes.focusedLabel,
                error: classes.labelError,
              },
            }}
            FormHelperTextProps={{
              classes: {
                root: classes.helperTextRoot,
                error: classes.helperTextError,
              },
            }}
            InputAdornmentProps={{
              classes: {
                root: classes.adornmentPosition,
              },
            }}
            fullWidth
            margin="dense"
            minDate={conditionalAttributes.min}
          />
        </ThemeProvider>
      </MuiPickersUtilsProvider>
    </div>
  )
}

export default DatePicker

DatePicker.propTypes = {
  /**
   * Theme object used for style customization.
   */
  theme: PropTypes.object,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  mode: PropTypes.oneOf([
    DATE_AND_TIME_FIELD_MODES.availableDate,
    DATE_AND_TIME_FIELD_MODES.dueDate,
    undefined,
  ]),
  showEmptyLabel: PropTypes.bool,
  editedField: PropTypes.bool,
  name: PropTypes.string,
  defaultValue: PropTypes.string,
  label: PropTypes.string,
  min: PropTypes.string,
}

DatePicker.defaultProps = {
  theme: {},
  required: false,
  disabled: false,
  mode: undefined,
  showEmptyLabel: false,
  editedField: false,
  min: '',
}
